import axios from 'axios';

export interface OracleInsight {
  summary: string;
  inversionFlag: boolean;
  inversionType?: 'inverted' | 'diluted' | 'weaponized' | 'none';
  warningLevel?: 'none' | 'mild' | 'severe';
}

/**
 * Generate Oracle Insights using OpenAI API
 */
export async function generateOracleInsight(
  word: string,
  protoRoot: string,
  originalMeaning: string,
  modernDefinition: string,
  ancientFrequency: number,
  modernFrequency: number,
  frequencyDivergence: number
): Promise<OracleInsight> {
  const apiKey = process.env.VITE_OPENAI_API_KEY || import.meta.env.VITE_OPENAI_API_KEY;

  if (!apiKey) {
    console.error('OpenAI API key not found');
    return {
      summary:
        'Oracle insights unavailable. Configure OpenAI API key to enable spiritual synthesis.',
      inversionFlag: false,
    };
  }

  const prompt = `You are KI CODE's Oracle—a linguistic frequency analyst specializing in etymological truth and spiritual meaning.

Analyze this word for linguistic inversion, dilution, or weaponization:

WORD: "${word}"
PROTO-ROOT: "${protoRoot}"
ORIGINAL MEANING: "${originalMeaning}"
MODERN DEFINITION: "${modernDefinition}"
ANCIENT FREQUENCY (Base 44): ${ancientFrequency}
MODERN FREQUENCY (Base 44): ${modernFrequency}
DIVERGENCE SCORE: ${frequencyDivergence} (0-44 scale)

TASK: Provide a 3-sentence spiritual summary that:
1. Explains the vibrational consequence of this word's shift
2. Identifies if the meaning has been inverted, diluted, or weaponized
3. Reveals the hidden frequency of truth vs. deception

Output as JSON: {"summary": "...", "inversionFlag": boolean, "inversionType": "inverted|diluted|weaponized|none", "warningLevel": "none|mild|severe"}`;

  try {
    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: prompt,
          },
        ],
        max_tokens: 300,
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const content = response.data.choices?.[0]?.message?.content || '';
    
    // Parse JSON from OpenAI response
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const insight = JSON.parse(jsonMatch[0]);
      return {
        summary: insight.summary || content,
        inversionFlag: insight.inversionFlag || false,
        inversionType: insight.inversionType || 'none',
        warningLevel: insight.warningLevel || 'none',
      };
    }

    return {
      summary: content,
      inversionFlag: frequencyDivergence > 22,
      inversionType: frequencyDivergence > 30 ? 'inverted' : 'none',
      warningLevel: frequencyDivergence > 30 ? 'severe' : 'none',
    };
  } catch (error) {
    console.error('OpenAI API error:', error);
    
    // Fallback analysis based on frequency divergence
    const hasInversion = frequencyDivergence > 22;
    return {
      summary: `The frequency has shifted by ${frequencyDivergence} points. ${
        hasInversion
          ? "This word's meaning has been significantly altered from its original truth."
          : "This word maintains relative fidelity to its original meaning."
      } The vibrational consequence reflects this divergence in how the word shapes consciousness.`,
      inversionFlag: hasInversion,
      inversionType: frequencyDivergence > 30 ? 'inverted' : 'diluted',
      warningLevel: frequencyDivergence > 30 ? 'severe' : 'mild',
    };
  }
}